import React, { ReactNode, useEffect, useState, useRef, useContext } from "react";
import { FieldArray, Form, Formik, FormikProps } from "formik";
import { SearchOutlined } from "@material-ui/icons";
import { makeStyles, Theme, Grid, useTheme, MenuItem } from "@material-ui/core";
import SearchIcon from "@material-ui/icons/Search";
import CustomTextField from "../../components/Input/CustomTextField";
import CustomButton from "../../components/Button/CustomButton";
import { TestRequestAdvancedSearchRQ } from "../../api/models/advanced-search/TestRequestAdvancedSearchRQ";
import {
  AdvancedSearchForm,
  ADVANCED_SEARCH_FORM_FIELDS,
  generateAdvancedSearch,
  getHelperText,
  MAX_FILTER_DEPTH,
  SEARCH_FORM_HELPER_TEXT,
  SEARCH_FORM_INITIAL_VALUES,
  SEARCH_FORM_LABELS,
} from "./AdvancedSearch.utils";
import RequestTypeSelect from "./RequestTypeSelect";
import CustomMultipleTextField from "../../components/Input/CustomMultipleTextField";
import MultipleTextFieldHelperText from "./MultipleTextFieldHelperText";
import CustomDropdown from "../../components/Input/CustomDropdown";
import GenericPermissionContext from "../../contexts/GenericPermissionContext";

const useStyles = makeStyles((theme: Theme) => ({
  smMarginTop: {
    [theme.breakpoints.down("sm")]: {
      marginTop: theme.spacing(3),
    },
  },
  buttonRound: {
    height: "initial",
    minWidth: "40px",
    borderRadius: "50%",
    padding: "12px 12px",
  },
  buttonTextLarge: {
    "& > span": {
      fontSize: "1.4rem",
    },
  },
  buttonTextSmall: {
    "& > span": {
      fontSize: "0.875rem",
    },
  },
  button: { width: "48%", height: "100%" },
  controlsContainer: {
    display: "flex",
    justifyContent: "space-between",
    paddingRight: 0,
    "& .MuiGrid-item ": {
      paddingRight: 0,
      "& > *": {
        height: "100%",
        width: "100%",
      },
    },
    [theme.breakpoints.down("sm")]: {
      marginTop: theme.spacing(1),
    },
  },
  form: {
    "& >.MuiGrid-container + .MuiGrid-container": {
      marginTop: theme.spacing(1),
      [theme.breakpoints.up("md")]: {
        marginTop: theme.spacing(6),
      },
    },
  },
  hidden: {
    visibility: "hidden",
  },
  inputContainer: {
    "& > * + *": {
      marginTop: theme.spacing(1),
    },
  },
  marginBottomM: {
    marginBottom: theme.spacing(3),
  },
  searchBar: {
    "& .MuiInputBase-root": {
      fontSize: "14px",
      paddingTop: "0.7rem",
      paddingBottom: "0.7rem",
    },
  },
}));

export interface AdvancedSearchFormProps {
  initialValues?: AdvancedSearchForm;
  onSubmit: (advancedSearch: TestRequestAdvancedSearchRQ) => void;
  setStoredSearchObject: (values: TestRequestAdvancedSearchRQ) => void;
  resetStoredSearchObject: () => void;
}

function AdvancedSearch({
  initialValues,
  onSubmit: handleSubmit,
  setStoredSearchObject,
  resetStoredSearchObject,
}: AdvancedSearchFormProps) {
  const classes = useStyles();
  const theme = useTheme();
  const [terms, setTerms] = useState<string[]>(initialValues?.terms || []);
  const multipleTextFieldRef = useRef<{ reset: () => void; showUnenteredTerm: () => string }>(null);

  const onSubmit = (values: AdvancedSearchForm): void => {
    values.terms = terms;
    handleSubmit(generateAdvancedSearch(values));
  };

  const permissions = useContext(GenericPermissionContext);

  return (
    <Formik
      initialValues={initialValues || SEARCH_FORM_INITIAL_VALUES}
      onSubmit={(values, formikProps) => {
        const unenteredTerm = multipleTextFieldRef.current?.showUnenteredTerm();
        const resetMultiTextfield = multipleTextFieldRef.current?.reset;

        if (unenteredTerm && resetMultiTextfield) {
          values.terms.push(unenteredTerm);
          resetMultiTextfield();
        }

        onSubmit(values);
        formikProps.setSubmitting(false);
      }}
    >
      {(formikProps: FormikProps<AdvancedSearchForm>): ReactNode => {
        const {
          errors,
          handleBlur,
          handleChange,
          touched,
          values,
          resetForm,
          submitForm,
          isSubmitting,
          setSubmitting,
          setFieldValue,
        } = formikProps;

        const clearSearch = () => {
          setTerms([]);
          resetStoredSearchObject();
          resetForm();
          setFieldValue("purpose", "");
          setFieldValue("products", [""]);
          setFieldValue("substrates", [""]);
          setFieldValue("testMethods", [""]);
          setFieldValue("priority", "ALL");
          submitForm();
          setSubmitting(false);
        };

        useEffect(() => {
          setStoredSearchObject(values as TestRequestAdvancedSearchRQ);
          onSubmit(values);
          setSubmitting(false);
        }, [terms]);

        const SearchPriorityFieldsTemplate = () => {
          const canViewPriority = !!permissions?.newTestRequest?.description?.viewTestRequestPriority;
          return (
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <CustomMultipleTextField
                  ref={multipleTextFieldRef}
                  autoFocus
                  className={classes.searchBar}
                  helperText={MultipleTextFieldHelperText()}
                  fullWidth
                  clearable
                  value={terms}
                  onChange={setTerms}
                  placeholder="Search Requests"
                  placeholderIcon={<SearchIcon />}
                />
              </Grid>
              <Grid className={classes.smMarginTop} item xs={6} md={canViewPriority ? 2 : 3}>
                <RequestTypeSelect
                  fieldName="purpose"
                  formikProps={formikProps}
                  helperText={getHelperText(formikProps, "purpose", SEARCH_FORM_HELPER_TEXT)}
                  label="Type of Request"
                />
              </Grid>
              {canViewPriority && (
                <Grid item xs={12} md={2}>
                  <CustomDropdown name="priority" onChange={handleChange} labelText="Priority" value={values.priority}>
                    <MenuItem value="ALL">ALL</MenuItem>
                    <MenuItem value="NONE">NONE</MenuItem>
                    <MenuItem value="LOW">LOW</MenuItem>
                    <MenuItem value="MEDIUM">MEDIUM</MenuItem>
                    <MenuItem value="HIGH">HIGH</MenuItem>
                  </CustomDropdown>
                </Grid>
              )}
              <Grid className={classes.controlsContainer} container item xs={12} md={canViewPriority ? 2 : 3}>
                <CustomButton
                  className={classes.button}
                  primary
                  type="submit"
                  startIcon={<SearchOutlined />}
                  text="Search"
                />
                <CustomButton className={classes.button} text="Clear Search" onClick={clearSearch} />
              </Grid>
            </Grid>
          );
        };

        return (
          <Form className={classes.form}>
            <Grid container spacing={2}>
              {ADVANCED_SEARCH_FORM_FIELDS.map((fieldName, fieldIndex) => (
                <Grid className={classes.inputContainer} item key={fieldName} xs={12} sm={6} md={3}>
                  <FieldArray
                    name={fieldName}
                    render={(arrayHelpers) =>
                      values?.[fieldName]?.length > 0 &&
                      values[fieldName].map((value, index) => {
                        const indexedFieldName = `${fieldName}.${index}`;
                        const hasMultipleTextfields =
                          fieldName === "substrates" || fieldName === "products" || fieldName === "testMethods";

                        if (hasMultipleTextfields && isSubmitting) {
                          if (value === "" && values[fieldName].length > 0) {
                            const textValues = values[fieldName].filter((fieldValue) => fieldValue !== "");
                            values[fieldName] = textValues.length > 0 ? textValues : [""];
                          }
                        }

                        return (
                          <Grid container direction="column" key={indexedFieldName} spacing={2}>
                            <Grid item xs={12}>
                              <CustomTextField
                                autoFocus={fieldIndex === 0 && index === 0}
                                onClear={() => {
                                  const newValue = values[fieldName].slice();
                                  newValue.splice(index, 1, "");
                                  setFieldValue(fieldName, newValue);
                                }}
                                error={!!(errors[fieldName] && touched[fieldName])}
                                fullWidth
                                helperText={getHelperText(formikProps, fieldName, SEARCH_FORM_HELPER_TEXT)}
                                id={fieldName}
                                label={SEARCH_FORM_LABELS[fieldName]}
                                name={indexedFieldName}
                                onChange={handleChange}
                                type="text"
                                value={value}
                              />
                            </Grid>
                            <Grid item container justify="center">
                              {index > 0 || values[fieldName].length > 1 ? (
                                <Grid item>
                                  <CustomButton
                                    className={[classes.buttonRound, classes.buttonTextLarge].join(" ")}
                                    onClick={() => {
                                      arrayHelpers.remove(index);
                                    }}
                                    text="-"
                                  />
                                </Grid>
                              ) : (
                                <></>
                              )}
                              {index < MAX_FILTER_DEPTH - 1 && (
                                <Grid item style={{ marginLeft: theme.spacing(1) }}>
                                  <CustomButton
                                    className={[classes.buttonRound, classes.buttonTextSmall].join(" ")}
                                    disabled={values[fieldName].length >= MAX_FILTER_DEPTH}
                                    onClick={() => {
                                      if (values[fieldName].length < MAX_FILTER_DEPTH) {
                                        arrayHelpers.insert(index + 1, "");
                                      }
                                    }}
                                    primary
                                    text="or"
                                  />
                                </Grid>
                              )}
                            </Grid>
                          </Grid>
                        );
                      })
                    }
                  />
                </Grid>
              ))}
              <Grid className={classes.inputContainer} item xs={12} sm={6} md={3}>
                <Grid container direction="column" spacing={2}>
                  <Grid item xs={12}>
                    <CustomTextField
                      onClear={() => {
                        setFieldValue("customer", "");
                      }}
                      error={!!(errors.customer && touched.customer)}
                      fullWidth
                      helperText={getHelperText(formikProps, "customer", SEARCH_FORM_HELPER_TEXT)}
                      id="customer"
                      label="Customer"
                      name="customer"
                      onBlur={handleBlur}
                      onChange={handleChange}
                      type="text"
                      value={values.customer}
                    />
                  </Grid>
                  <Grid className={classes.hidden} item container justify="center">
                    <CustomButton className={classes.buttonRound} primary text="or" />
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
            {SearchPriorityFieldsTemplate()}
          </Form>
        );
      }}
    </Formik>
  );
}

export default AdvancedSearch;
